import classNames from 'classnames/bind';
import { useId, useState } from 'react';
import Select, {
  ClearIndicatorProps,
  ControlProps,
  MultiValueRemoveProps,
  OptionProps,
  PlaceholderProps,
  SingleValueProps,
  components,
} from 'react-select';
import Createable from 'react-select/creatable';

import { ChevronDown, ChevronUp, WarningNotifRed } from '../../assets/icons/Icons';
import isMobile from '../../helpers/isMobile';
import Styles from './floatingdropdown.module.scss';

const cx = classNames.bind(Styles);
const KG_BLUE = '#002acf';

export interface IOption {
  value: string | number | null;
  label: string;
}

type Props<T extends IOption> = {
  options: Array<T>;
  onChange?: (value: T | null) => void;
  showLabel?: boolean;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  id?: string;
  defaultOption?: T | string | number | Array<T | string | number>;
  value?: T | string | number;
  fullWidth?: boolean;
  isCreateable?: boolean;
  validateNewItem?: (inputValue: string, selectValue: unknown) => boolean;
  newOption?: (inputValue: string) => void;
  desktopWidth?: string;
  mobileWidth?: string;
  error?: boolean;
  errorMessage?: string;
};

type MultiProps<T extends IOption> = Props<T> & {
  isMulti: true;
  onChange?: (value: T[]) => void;
  defaultOption: Array<T | string | number>;
};

function FloatingDropdown<T extends IOption>(props: Props<T> | MultiProps<T>) {
  const {
    options,
    disabled,
    label,
    id,
    onChange,
    fullWidth,
    isCreateable,
    defaultOption,
    value,
    desktopWidth,
    mobileWidth,
    validateNewItem,
    newOption,
    error,
    errorMessage,
  } = props;

  let isMulti = false;
  if ('isMulti' in props) {
    isMulti = true;
  }

  const itemId = id || useId();
  const [placeholder, setPlaceholder] = useState(label || props.placeholder);

  const mobile = isMobile();

  const FloatingDropdownStyles = {
    container: (base) => ({
      ...base,
      display: fullWidth ? 'block' : 'inline-block',
      width: fullWidth ? 'auto' : (desktopWidth ?? 'fit-content'),
      '@media (max-width: 768.98px)': {
        width: fullWidth ? 'auto' : (mobileWidth ?? 'fit-content'),
        minWidth: fullWidth || mobileWidth ? '0' : cx('formElementWidth'),
      },
      minWidth: fullWidth || desktopWidth ? '0' : cx('formElementWidth'),
      minHeight: cx('formElementHeight'),
      verticalAlign: 'bottom',
    }),
    clearIndicator: (base) => ({
      ...base,
      padding: '0',
      ':hover': {
        color: 'grey',
      },
      color: 'var(--color-black)',
    }),
    multiValue: (base) => ({
      ...base,
      backgroundColor: '#f0f0f0',
    }),
    multiValueLabel: (base) => ({
      ...base,
      backgroundColor: '#f0f0f0',
      padding: 0,
      paddingRight: '0.1rem',
    }),
    multiValueRemove: (base) => ({
      ...base,
      backgroundColor: '#f0f0f0',
      padding: '0',
      ':hover': {
        color: 'grey',
      },
      height: '1.25rem',
      alignSelf: 'center',
      color: 'var(--color-black)',
    }),
    control: (base) => ({
      ...base,
      cursor: 'pointer',
    }),
    input: (base) => ({
      ...base,
      padding: '0px',
      margin: '0px',
      caretColor: KG_BLUE,
      fontSize: '0.875rem',
      lineHeignt: '1.5rem',
    }),
    placeholder: (base) => ({
      ...base,
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    menu: (styles) => ({
      ...styles,
      width: desktopWidth ?? '100%',
      overflowY: 'hidden',
      border: '1px solid #15191B',
      borderRadius: '2px',
      boxShadow: 'none',
      '@media (max-width: 768.98px)': {
        width: mobileWidth ?? '100%',
        minWidth: '150px',
      },
      zIndex: '2',
      // right: customMenuRightAlign ? '0' : 'auto',
    }),
    menuList: () => ({
      overflowY: 'scroll',
      maxHeight: '21.25rem', //340px,
      padding: '0.5rem 0',
      scrollbarWidth: 'none', // Firefox,
      msOverflowStyle: 'none', // IE 10+
    }),
    option: () => ({
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    }),
    valueContainer: (base) => ({
      ...base,
      padding: '0px',
      margin: '0px',
      maxHeight: '3rem',
      height: '3rem',
      overflowY: 'scroll',
      '::-webkit-scrollbar': {
        width: 0,
        background: 'transparent',
      },
    }),
  };

  let selectValue: T | undefined;
  if (typeof value === 'string' || typeof value === 'number') {
    selectValue = options.find((option) => option.value === value);
  } else {
    selectValue = value;
  }

  let selectDefault: T | undefined | (string | number | T)[];
  if (typeof defaultOption === 'string' || typeof defaultOption === 'number') {
    selectDefault = (options || []).find((option) => option.value === defaultOption);
  } else if (!defaultOption && options) {
    selectDefault = (options || []).find((option) => option.value === '');
  } else {
    selectDefault = defaultOption;
  }

  const dropdownProps = {
    components: {
      Control,
      Placeholder,
      DropdownIndicator: () => null,
      IndicatorSeparator: () => null,
      SingleValue,
      Option,
      ClearIndicator,
      MultiValueRemove,
    },
    className: cx('form-element', 'floating-dropdown', { fw: fullWidth }, { 'floating-dropdown--error': error }),
    options,
    isDisabled: disabled,
    id: itemId,
    onChange: onChange as any,
    placeholder: placeholder,
    styles: FloatingDropdownStyles as any,
    onMenuOpen: () => {
      !mobile && setPlaceholder('');
    },
    onMenuClose: () => {
      !mobile && setPlaceholder(label || props.placeholder);
    },
    isSearchable: !mobile,
    defaultValue: selectDefault,
    value: selectValue,
    'aria-label': label,
    error: error,
    errorMessage: errorMessage,
    isMulti,
  };

  return isCreateable ? (
    <>
      <Createable
        createOptionPosition='last'
        isValidNewOption={validateNewItem}
        onCreateOption={newOption}
        {...dropdownProps}
        isMulti={false}
      />
      {error ? (
        <p className={cx('error-message')}>
          {WarningNotifRed} {errorMessage}
        </p>
      ) : null}
    </>
  ) : (
    <>
      {/* @ts-expect-error: isMulti is not a valid prop for Select */}
      <Select {...dropdownProps} />
      {error ? (
        <p className={cx('error-message')}>
          {WarningNotifRed} {errorMessage}
        </p>
      ) : null}
    </>
  );
}

//TODO: we can probably remove most of these and replace with built in classNamePrefix: https://react-select.com/styles#using-classnames

function Control<T>(props: ControlProps<T, false> & { isDisabled?: boolean; menuIsOpen?: boolean }) {
  const icon = props.menuIsOpen ? ChevronUp : ChevronDown;

  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.Control
      {...props}
      className={cx('floating-dropdown__control', {
        'floating-dropdown__control--disabled': props.isDisabled,
      })}>
      {props.children}
      <span>{icon}</span>
    </components.Control>
  );
}

function Placeholder<T>(props: PlaceholderProps<T, false> & { isDisabled?: boolean }) {
  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.Placeholder
      {...props}
      className={cx('floating-dropdown__placeholder', {
        'floating-dropdown__placeholder--disabled': props.isDisabled,
      })}>
      {props.children}
    </components.Placeholder>
  );
}

function SingleValue<T>(props: SingleValueProps<T, false>) {
  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.SingleValue {...props} className={cx('floating-dropdown__single-value')}>
      {props.children}
    </components.SingleValue>
  );
}

function Option<T>(props: OptionProps<T, false> & { isDisabled?: boolean; isSelected?: boolean }) {
  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.Option
      {...props}
      className={cx('floating-dropdown__option', {
        'floating-dropdown__option--disabled': props.isDisabled,
        'floating-dropdown__option--selected': props.isSelected,
      })}>
      {props.children}
    </components.Option>
  );
}

function ClearIndicator<T>(props: ClearIndicatorProps<T, false>) {
  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.ClearIndicator {...props}>
      <i className='fa-sharp fa-regular fa-circle-xmark' />
    </components.ClearIndicator>
  );
}

function MultiValueRemove<T>(props: MultiValueRemoveProps<T, false>) {
  return (
    // @ts-ignore - //TODO: react-select components types not working properly with react-19
    <components.MultiValueRemove {...props}>
      <i className='fa-sharp fa-solid fa-xmark' />
    </components.MultiValueRemove>
  );
}
export default FloatingDropdown;
